import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, Mail, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
interface LoginScreenProps {
  onClose: () => void;
  onLoginSuccess: () => void;
}
type LoginStep = 'methods' | 'phone_input' | 'sms_code' | 'success';
export function LoginScreen({
  onClose,
  onLoginSuccess
}: LoginScreenProps) {
  const [step, setStep] = useState<LoginStep>('methods');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+48');
  const [smsCode, setSmsCode] = useState(['', '', '', '', '', '']);
  const {
    playClick,
    playSuccess
  } = useClickSound();
  const handleMethodClick = (method: string) => {
    playClick();
    if (method === 'phone') {
      setStep('phone_input');
    } else {
      // Simulate OAuth login
      setStep('success');
      playSuccess();
      setTimeout(() => onLoginSuccess(), 1500);
    }
  };
  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.length >= 9) {
      playClick();
      setStep('sms_code');
    }
  };
  const handleSmsChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...smsCode];
    newCode[index] = value;
    setSmsCode(newCode);
    // Auto-advance
    if (value && index < 5) {
      const nextInput = document.getElementById(`sms-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    // Check if complete
    if (index === 5 && value && newCode.every(c => c !== '')) {
      playSuccess();
      setStep('success');
      setTimeout(() => onLoginSuccess(), 1500);
    }
  };
  const handleSmsKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !smsCode[index] && index > 0) {
      const prevInput = document.getElementById(`sms-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col bg-zen-bg text-zen-text" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} data-id="element-599">
      {/* Header */}
      <div className="w-full flex items-center p-6" data-id="element-600">
        <button onClick={() => {
        playClick();
        if (step === 'phone_input') setStep('methods');else if (step === 'sms_code') setStep('phone_input');else onClose();
      }} className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-601">
          <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-602" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center px-6 pt-4 max-w-md mx-auto w-full" data-id="element-603">
        <AnimatePresence mode="wait" data-id="element-604">
          {/* METHODS STEP */}
          {step === 'methods' && <motion.div key="methods" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full flex flex-col items-center" data-id="element-605">
              <h1 className="text-2xl font-light mb-2 text-center" data-id="element-606">
                Witaj ponownie
              </h1>
              <p className="text-sm text-slate-500 mb-10 text-center" data-id="element-607">
                Zaloguj się, aby zsynchronizować swoją aurę.
              </p>

              <div className="w-full space-y-3" data-id="element-608">
                <button onClick={() => handleMethodClick('google')} className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-center space-x-3 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-609">
                  <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" data-id="element-610" />
                  <span className="font-medium text-sm" data-id="element-611">
                    Kontynuuj z Google
                  </span>
                </button>

                <button onClick={() => handleMethodClick('apple')} className="w-full bg-black text-white rounded-2xl p-4 flex items-center justify-center space-x-3 hover:bg-gray-900 transition-colors shadow-sm" data-id="element-612">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" data-id="element-613">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.04 2.26-.79 3.59-.79 1.56 0 2.95.69 3.77 1.82-3.21 1.93-2.68 5.95.49 7.21-.76 1.85-1.93 3.01-2.93 3.93zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-614" />
                  </svg>
                  <span className="font-medium text-sm" data-id="element-615">Kontynuuj z Apple</span>
                </button>

                <button onClick={() => handleMethodClick('facebook')} className="w-full bg-[#1877F2] text-white rounded-2xl p-4 flex items-center justify-center space-x-3 hover:bg-[#166FE5] transition-colors shadow-sm" data-id="element-616">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" data-id="element-617">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-618" />
                  </svg>
                  <span className="font-medium text-sm" data-id="element-619">
                    Kontynuuj z Facebook
                  </span>
                </button>

                <div className="flex items-center w-full my-6" data-id="element-620">
                  <div className="flex-1 border-t border-slate-200" data-id="element-621"></div>
                  <span className="px-4 text-xs text-slate-400 uppercase tracking-widest" data-id="element-622">
                    lub
                  </span>
                  <div className="flex-1 border-t border-slate-200" data-id="element-623"></div>
                </div>

                <button onClick={() => handleMethodClick('phone')} className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-center space-x-3 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-624">
                  <Phone className="w-5 h-5 text-slate-600" data-id="element-625" />
                  <span className="font-medium text-sm text-slate-700" data-id="element-626">
                    Użyj numeru telefonu
                  </span>
                </button>
              </div>
            </motion.div>}

          {/* PHONE INPUT STEP */}
          {step === 'phone_input' && <motion.div key="phone_input" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full flex flex-col" data-id="element-627">
              <h1 className="text-2xl font-light mb-2" data-id="element-628">Podaj numer telefonu</h1>
              <p className="text-sm text-slate-500 mb-8" data-id="element-629">
                Wyślemy Ci kod weryfikacyjny SMS.
              </p>

              <form onSubmit={handlePhoneSubmit} className="w-full" data-id="element-630">
                <div className="flex space-x-3 mb-8" data-id="element-631">
                  <div className="relative" data-id="element-632">
                    <select value={countryCode} onChange={e => setCountryCode(e.target.value)} className="appearance-none bg-white border border-slate-200 rounded-2xl py-4 pl-4 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zen-accent/50 shadow-sm" data-id="element-633">
                      <option value="+48" data-id="element-634">🇵🇱 +48</option>
                      <option value="+1" data-id="element-635">🇺🇸 +1</option>
                      <option value="+44" data-id="element-636">🇬🇧 +44</option>
                      <option value="+49" data-id="element-637">🇩🇪 +49</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500" data-id="element-638">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-639">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-640"></path>
                      </svg>
                    </div>
                  </div>

                  <input type="tel" placeholder="000 000 000" value={phone} onChange={e => setPhone(e.target.value.replace(/[^\d\s]/g, ''))} className="flex-1 bg-white border border-slate-200 rounded-2xl px-4 py-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zen-accent/50 shadow-sm" autoFocus data-id="element-641" />
                </div>

                <button type="submit" disabled={phone.length < 9} className="w-full bg-zen-sage text-white rounded-full py-4 text-sm font-bold hover:bg-zen-sage/90 transition-colors shadow-lg shadow-zen-sage/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2" data-id="element-642">
                  <span data-id="element-643">Dalej</span>
                  <ChevronRight className="w-4 h-4" data-id="element-644" />
                </button>
              </form>
            </motion.div>}

          {/* SMS CODE STEP */}
          {step === 'sms_code' && <motion.div key="sms_code" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full flex flex-col items-center" data-id="element-645">
              <h1 className="text-2xl font-light mb-2 text-center" data-id="element-646">
                Wpisz kod z SMS
              </h1>
              <p className="text-sm text-slate-500 mb-8 text-center" data-id="element-647">
                Wysłaliśmy 6-cyfrowy kod na numer
                <br data-id="element-648" />
                <span className="font-medium text-slate-700" data-id="element-649">
                  {countryCode} {phone}
                </span>
              </p>

              <div className="flex justify-center space-x-2 mb-8 w-full" data-id="element-650">
                {smsCode.map((digit, idx) => <input key={idx} id={`sms-${idx}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleSmsChange(idx, e.target.value)} onKeyDown={e => handleSmsKeyDown(idx, e)} className="w-12 h-14 bg-white border border-slate-200 rounded-xl text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-zen-accent/50 shadow-sm" autoFocus={idx === 0} data-id="element-651" />)}
              </div>

              <button className="text-xs text-zen-accent font-medium hover:underline" data-id="element-652">
                Wyślij kod ponownie
              </button>
            </motion.div>}

          {/* SUCCESS STEP */}
          {step === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="w-full flex flex-col items-center justify-center pt-20" data-id="element-653">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6" data-id="element-654">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-655" />
              </div>
              <h1 className="text-2xl font-light mb-2 text-center" data-id="element-656">
                Zalogowano pomyślnie
              </h1>
              <p className="text-sm text-slate-500 text-center" data-id="element-657">
                Wracamy do aplikacji...
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}